import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { Case } from "../../hooks/useCases";
import { useLex } from "../../lib/theme/ThemeProvider";

interface Props {
  caseRow: Case;
}

const LS_KEY = "berserkr.caseHeaderCollapsed";

/**
 * Case header — collapsible. Collapsed it is a slim one-line strip (case number,
 * caption, status); expanded it is the full block. The choice persists across
 * pages and sessions via localStorage.
 */
function CaseHeader({ caseRow }: Props) {
  const location = useLocation();
  const lex = useLex();
  const isOnCommandPage = location.pathname.startsWith(`/case/${caseRow.id}/`);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(LS_KEY) === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  if (collapsed) {
    return (
      <header className="rounded border border-steel-dark bg-iron/70 px-4 py-2 backdrop-blur-sm">
        <div className="flex items-center gap-3 overflow-hidden">
          <Link to="/" className="shrink-0 font-display text-sm font-light uppercase tracking-[0.12em] text-bone-mute hover:text-bone">
            ← campaigns
          </Link>
          {isOnCommandPage ? (
            <Link to={`/case/${caseRow.id}`} className="shrink-0 font-display text-sm font-light uppercase tracking-[0.12em] text-ember-bright/80 hover:text-ember-bright">
              ← throne
            </Link>
          ) : null}
          <span className="shrink-0 font-display text-sm font-light uppercase tracking-[0.2em] text-bone-mute">{caseRow.case_number}</span>
          <span className="min-w-0 truncate font-display text-sm font-light text-bone">{caseRow.caption}</span>
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            className="ml-auto shrink-0 font-display text-sm font-light uppercase tracking-[0.2em] text-bone-mute hover:text-ember-bright"
          >
            case ▾
          </button>
        </div>
      </header>
    );
  }

  return (
    <header className="rounded border border-steel-dark bg-iron/70 px-6 py-5 backdrop-blur-sm">
      <div className="mb-2 flex items-center gap-4">
        <Link
          to="/"
          className="inline-block font-display text-base font-light uppercase tracking-[0.16em] text-bone-mute hover:text-bone"
        >
          ← campaigns
        </Link>
        {isOnCommandPage ? (
          <Link
            to={`/case/${caseRow.id}`}
            className="inline-block font-display text-base font-light uppercase tracking-[0.16em] text-ember-bright/80 hover:text-ember-bright"
          >
            ← back to the {lex.surfaceName.toLowerCase()}
          </Link>
        ) : null}
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="ml-auto font-display text-sm font-light uppercase tracking-[0.2em] text-bone-mute hover:text-ember-bright"
        >
          collapse ▴
        </button>
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-mute">
            {caseRow.case_number}
          </p>
          <Link
            to={`/case/${caseRow.id}`}
            className="block hover:opacity-80"
            aria-label={lex.ui.homeAria}
          >
            <h1
              className="mt-1 font-display text-2xl font-light leading-tight text-bone md:text-3xl"
              style={{ textShadow: "0 2px 16px rgba(0,0,0,0.6)" }}
            >
              {caseRow.caption}
            </h1>
          </Link>
          <p className="mt-1 font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute/80">
            {caseRow.court} // {caseRow.jurisdiction}
          </p>
        </div>
        <StatusBlock caseRow={caseRow} />
      </div>
    </header>
  );
}

function StatusBlock({ caseRow }: { caseRow: Case }) {
  const styles: Record<Case["status"], string> = {
    active: "border-ember-bright/60 text-ember-bright",
    stayed: "border-bone-mute/60 text-bone-mute",
    closed: "border-bone-mute/40 text-bone-mute/70",
    dismissed: "border-bone-mute/40 text-bone-mute/70",
    on_appeal: "border-blue-bright/60 text-blue-bright",
  };
  return (
    <div className="text-right">
      <span
        className={`inline-block rounded border px-2 py-0.5 font-display text-base font-light uppercase tracking-[0.12em] ${styles[caseRow.status]}`}
      >
        {caseRow.status.replace("_", " ")}
      </span>
      <p className="mt-2 font-display text-base font-light uppercase tracking-[0.12em] text-bone-mute/80">
        filed{" "}
        {new Date(caseRow.filed_at).toLocaleDateString("en-US", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}
      </p>
    </div>
  );
}

export default CaseHeader;
